"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Turning a Hungarian name into a machine-readable identifier.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Two callers with two different shapes: a metafield **key** (`ugyfel_hivatkozas`, underscores,
 * 40 characters, `[a-z][a-z0-9_]*`) and a tenant or workspace **slug** (`loginet-systems`,
 * hyphens, 3–64 characters, alphanumeric at both ends). The separator and the length differ; the
 * accent folding does not, so it lives here once.
 *
 * ⚠️ **This module exists because the fold was about to be written a second time.** C10 put it in
 * `metafields.ts` with a paragraph explaining why the naive version is wrong twice over — and a
 * slug generator that reimplemented it would get one of those two wrong, silently, for exactly the
 * two letters unique to Hungarian. CLAUDE.md's own words: *"a duplicated permission predicate is
 * the worst case — the copy that drifts is the one deciding what a UI offers while the server
 * quietly disagrees."* Same shape, lower stakes, same fix.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SLUG_MAX = exports.SLUG_MIN = void 0;
exports.foldHungarian = foldHungarian;
exports.isValidSlug = isValidSlug;
exports.slugFrom = slugFrom;
/**
 * ⚠️⚠️ **LOWER-CASE FIRST, THEN FOLD, AND THE ORDER IS LOAD-BEARING.** The map holds only
 * lower-case accented letters, so folding before lower-casing drops every `Ü` and `Ő` — which is
 * what anybody refactoring this would do. C10 asserts the order in its own tests and so does
 * `__tests__/text.test.ts`.
 *
 * ⚠️ **The map is explicit rather than clever, and `ő`/`ű` are the reason.** A generic
 * `normalize('NFD')` + strip-combining-marks fold gets those two wrong in older runtimes: `ő`
 * decomposes to `o` + double acute, which some ICU versions do not classify as a mark, so the
 * accent survives and the identifier contains a character the column's check constraint refuses.
 */
const ACCENTS = {
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ö: 'o',
    ő: 'o',
    ú: 'u',
    ü: 'u',
    ű: 'u',
};
/** Lower-cased and accent-folded. Everything else is left alone — the caller decides separators. */
function foldHungarian(value) {
    return value
        .toLowerCase()
        .replace(/[áéíóöőúüű]/g, (character) => ACCENTS[character] ?? character);
}
/**
 * The shape `tenants.slug` and `workspaces.slug` both enforce (migration 0003):
 * `^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$`.
 *
 * ⚠️ **Three characters minimum, and it comes from the regex rather than from taste** — the middle
 * quantifier is `{1,62}` between two mandatory alphanumerics. A two-character organisation name is
 * therefore not sluggable, which the caller has to handle rather than discover from a 500.
 */
exports.SLUG_MIN = 3;
exports.SLUG_MAX = 64;
/** Does this string satisfy the column's check constraint? Asked before the round trip. */
function isValidSlug(slug) {
    return /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(slug);
}
/**
 * Derive a slug from a display name.
 *
 * `LogiNet Systems Kft.` → `loginet-systems-kft`, `Ügyfélszolgálat` → `ugyfelszolgalat`.
 *
 * ⚠️ **It can return the EMPTY STRING, and the caller must handle that.** `"???"` folds to nothing
 * and `"Kft"` — three characters — is fine while `"AB"` is not. Returning `''` rather than
 * fabricating `tenant-1` keeps the decision where a sentence can be written about it, which is the
 * convention `metafieldKeyFrom` already set for the same reason.
 *
 * ⚠️ **Trimmed AFTER the slice, not before.** Cutting `loginet-systems-` to length can leave a
 * trailing hyphen, and a trailing hyphen fails the check constraint — so the order here is as
 * load-bearing as the fold's, and for a failure that would only appear for names longer than 64
 * characters.
 */
function slugFrom(name) {
    const slug = foldHungarian(name)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, exports.SLUG_MAX)
        .replace(/-+$/g, '');
    return slug.length >= exports.SLUG_MIN ? slug : '';
}
//# sourceMappingURL=text.js.map